import { AxDropdown, Direction } from "@axess/limestone";
import { AxDatePicker, Cell, Mode } from "../../Components/AxDatepicker/AxDatepicker";
import { AxInit, ModuleName } from "../../Lib/AxSystem/AxInit";
import { AxLoading } from "../../Limestone/AxLoading/AxLoading";

interface ILessonsInitData {
    startOfWeek: number;
    startDate: string;
    endDate?: string;
    datePattern: string;
    lang: string;
}

@ModuleName("Lessons")
export class Lessons implements AxInit {
    public datePicker: AxDatePicker;

    public _dateDropDown: AxDropdown = new AxDropdown();
    public _activityDropdown: AxDropdown = new AxDropdown();
    public _ageGroupDropdown: AxDropdown = new AxDropdown();
    public _skillLevelDropdown: AxDropdown = new AxDropdown();
    public _productOptionsDropdown: AxDropdown = new AxDropdown();

    private groupElement: HTMLElement | null = null;
    private buttons: HTMLButtonElement[] = [];

    private initData: ILessonsInitData;

    private selectedType?: number;
    private selectedActivity?: number;
    private selectedAgeGroup?: number;
    private selectedLevel?: number;
    private selectedDate?: string;
    private selectedOption?: number[] | undefined;

    constructor(private _axLoading = new AxLoading()) { }

    AxInit(init?: ILessonsInitData): void {
        this.initData = init as ILessonsInitData;
        this.datePicker = new AxDatePicker();

        this.datePicker.Init({
            StartOfWeek: init?.startOfWeek ?? 0,
            Lang: init?.lang ?? 'en',
            StartDate: moment(init?.startDate, "YYYY-MM-DD"),
            DayRange: 0,
            Target: '[data-lesson="datepicker"]',
            Mode: Mode.Inline,
            EndDate: undefined,
            monthsShown: 1,
            BlockedDates: [],
        });

        if ($('.scroll-container').length > 0) {
            setupHorizontalScroll($('.scroll-container').get(0) as HTMLElement);
        }

        this.datePicker.ActivationSubject.subscribe(x => { this.setDate(x); });

        this._dateDropDown.init(`[data-lesson="datepicker-dropdown"]`, {
            searchBar: false,
            multiSelect: false,
        });

        this._activityDropdown.init(`[data-lesson="activity-dropdown"]`, {
            searchBar: false,
            multiSelect: true,
            onSelect: ($parent, value: string | string[]) => {
                const $popper = this.getPopper(this._skillLevelDropdown);
                const $listItems = $($popper.find('.ax-dropdown__item'));
                const activeList = $listItems.filter(`[data-type="${value}"]`);
                this._skillLevelDropdown.resetSelection();
                this.selectedLevel = undefined;

                if (activeList.length == 0) {
                    $('[data-lesson="skill-dropdown"]').prop('disabled', true);
                    return;
                }

                $('[data-lesson="skill-dropdown"]').removeAttr('disabled');

                $listItems.addClass('d-none');
                activeList.removeClass('d-none');

                this.selectedActivity = parseInt(value as string);

                this.filterList();
            },
        });

        this._ageGroupDropdown.init(`[data-lesson="ageGroup-dropdown"]`, {
            searchBar: false,
            multiSelect: true,
            onSelect: ($parent, value: string | string[]) => {
                this.selectedAgeGroup = parseInt(value as string);

                this.filterList();
            },
        });

        this._skillLevelDropdown.init(`[data-lesson="skill-dropdown"]`, {
            searchBar: false,
            multiSelect: true,
            onSelect: ($parent, value: string | string[]) => {
                this.selectedLevel = parseInt(value as string);

                this.filterList();
            },
        });

        this._productOptionsDropdown.init('[ data-lesson="product-options"]', {
            searchBar: false,
            multiSelect: true,
            onChange: ($parent, affectedValue: string, direction: Direction, value: string | string[]) => {
                this.selectedOption = this.getCalculatedOption(value as string[]);
                this.filterList();
            },
        })

        $('[data-lessons="reset-filter"]').on('click', () => {
            this.resetFilter();
        });

        this.groupElement = document.querySelector('[role="radiogroup"]');
        if (!this.groupElement) return;

        this.buttons = Array.from(this.groupElement.querySelectorAll('[role="radio"]'));

        this.updateEntryCount();
        this.setupEventListeners();
    }

    private setDate(cell: Cell) {
        if (cell != null) {
            this._dateDropDown.setValue(cell.Date.format(this.initData.datePattern));
            this._dateDropDown.close();

            this.selectedDate = cell.Date.format('YYYY-MM-DD');
            this.getSlots();
        }
    }

    private getSlots() {
        const $target = $('[data-lesson="lesson-container"]');
        $target.html('');

        $('[data-lessons="result-count-wrapper"]').addClass('d-none');

        this._axLoading.ShowLoadingIndicator($target[0], true);

        AxessViewer.loadPartialView(`/Products/Lessons/_GetLessons`, {
            date: this.selectedDate
        }, (resp) => {
            this._axLoading.HideLoadingIndicator($target[0]);
            $target.html(resp.returnView);
            $('[data-lessons="result-count-wrapper"]').removeClass('d-none');
            this.filterList();
        })
    }

    private setupEventListeners(): void {
        this.buttons.forEach((button) => {
            button.addEventListener('click', () => {
                this.selectRadio(button);
            });

            button.addEventListener('keydown', (e: KeyboardEvent) => {
                const index = this.buttons.indexOf(button);
                let nextIndex: number;

                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    nextIndex = (index + 1) % this.buttons.length;
                } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    nextIndex = (index - 1 + this.buttons.length) % this.buttons.length;
                } else {
                    return;
                }

                e.preventDefault();

                const nextButton = this.buttons[nextIndex];
                this.selectRadio(nextButton);
                nextButton.focus();
            });
        });
    }

    private selectRadio(selectedButton: HTMLButtonElement): void {
        const $selectedButton = $(selectedButton);
        const alreadySelected = $selectedButton.attr('aria-checked') === 'true';

        this.buttons.forEach((button) => {
            const $button = $(button);
            const type = $button.attr('data-value');

            if (button !== selectedButton || alreadySelected) {
                $button.removeClass('ax-btn-primary').addClass('ax-btn-secondary--dark');
                button.setAttribute('aria-checked', 'false');
                button.setAttribute('tabindex', '-1');
            } else {
                $button.removeClass('ax-btn-secondary--dark').addClass('ax-btn-primary');
                button.setAttribute('aria-checked', 'true');
                button.setAttribute('tabindex', '0');
            }
        });

        this.selectedType = parseInt($selectedButton.attr('data-value') as string);
        if (alreadySelected) {
            this.selectedType = undefined;
            this.toggleListEntry(`[data-lesson="list-entry"]`, true);
        }

        this.filterList();
    }

    private updateEntryCount() {
        $('[data-lessons="result-count"]').html($('[data-lesson="list-entry"].d-flex').length.toString());
    }

    private filterList() {
        let filterStr = '';
        let filterActive = false;
        const dateSet = this.selectedDate != null;

        this.toggleListEntry(`[data-lesson="list-entry"]`, false);

        if (dateSet) {
            filterActive = true;
        }

        if (this.selectedType !== undefined) {
            filterActive = true;
            filterStr += `[data-lesson-type="${this.selectedType}"]`;
        }

        if (this.selectedActivity) {
            filterActive = true;
            filterStr += `[data-lesson-activity="${this.selectedActivity}"]`;
        }

        if (this.selectedAgeGroup) {
            filterActive = true;
            filterStr += `[data-lesson-age-group-${this.selectedAgeGroup}]`;
        }

        if (this.selectedLevel) {
            filterActive = true;
            filterStr += `[data-lesson-level="${this.selectedLevel}"]`;
        }

        if (this.selectedOption) {
            filterActive = true;
            const matches = this.selectedOption.map((bit) => `[data-lesson-option="${bit}"]`).join(', ');
            filterStr += `:is(${matches})`;
        }

        $('[data-lessons="reset-filter"]').toggleClass('d-none', !filterActive);
        this.toggleListEntry(`[data-lesson="list-entry"]${filterStr}`, true);

        const itemsAvailable = $('[data-lesson="list-entry"].d-flex').length > 0;
        $('[data-lesson="not-found"]').toggleClass('d-none', itemsAvailable).toggleClass('d-flex', !itemsAvailable);

        this.updateEntryCount();
    }

    private resetFilter() {
        const dateSet = this.selectedDate != null;

        this.selectedType = undefined;
        this.selectedActivity = undefined;
        this.selectedAgeGroup = undefined;
        this.selectedLevel = undefined;
        this.selectedDate = undefined;

        this._dateDropDown.resetSelection();
        this._activityDropdown.resetSelection();
        this._ageGroupDropdown.resetSelection();
        this._skillLevelDropdown.resetSelection();
        this._productOptionsDropdown.resetSelection();

        $(this.buttons).removeClass('ax-btn-primary').addClass('ax-btn-secondary--dark').attr('aria-checked', 'false').attr('tabindex', '-1');

        if (dateSet) {
            this.getSlots();
        } else {
            this.filterList();
        }
    }

    private toggleListEntry(selector: string, active: boolean) {
        $(selector).toggleClass('d-none', !active).toggleClass('d-flex', active);
    }

    private getPopper(dropdown: AxDropdown): JQuery<HTMLElement> {
        //@ts-ignore
        const instance = dropdown.getElements()[0]?._tippy as Instance<Props>;
        let $popper = $(instance?.popper);

        return $popper;
    }

    private getCalculatedOption(selectedValues: string[]): number[] | undefined {
        if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
            return;
        }

        const extrasMask = selectedValues.reduce((acc, id) => {
            return acc | (Number(id as string) || 0);
        }, 0);

        if (extrasMask === 0) return;

        const baseProductMask = this.selectedType === 2 ? 8 : 4;
        const validOptions: number[] = [];
        for (let extra = 1; extra <= 3; extra++) {
            if ((extra & extrasMask) === extrasMask) {
                validOptions.push(baseProductMask + extra);
            }
        }

        if (validOptions.length === 0) return;

        return validOptions;
    }
}

export function setupHorizontalScroll(container: HTMLElement): () => void {
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const handleWheel = (e: WheelEvent) => {
        if (e.deltaY !== 0) {
            e.preventDefault();
            container.scrollLeft += e.deltaY;
        }
    };

    const handleMouseDown = (e: MouseEvent) => {
        isDown = true;
        startX = e.pageX - container.offsetLeft;
        scrollLeft = container.scrollLeft;
        container.style.cursor = 'grabbing';
    };

    const handleMouseLeaveOrUp = () => {
        isDown = false;
        container.style.cursor = 'grab';
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 1.5;
        container.scrollLeft = scrollLeft - walk;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeaveOrUp);
    container.addEventListener('mouseup', handleMouseLeaveOrUp);
    container.addEventListener('mousemove', handleMouseMove);

    return () => {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mouseleave', handleMouseLeaveOrUp);
        container.removeEventListener('mouseup', handleMouseLeaveOrUp);
        container.removeEventListener('mousemove', handleMouseMove);
    };
}